import { useEffect, useState } from "react";
import { API } from "../lib/api";
import { PageHeader, Spinner, StatCard } from "../components/common";
import { formatINR } from "../lib/helpers";
import { TrendingUp } from "lucide-react";
import {
  BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, ResponsiveContainer,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import ChartTooltip from "../components/ChartTooltip";

const COLORS = ["#F59E0B", "#3B82F6", "#10B981", "#8B5CF6", "#EF4444", "#64748B"];

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { API.get("/analytics/overview").then((r) => setData(r.data)); }, []);
  if (!data) return <Spinner />;

  const tip = { content: <ChartTooltip/> };

  return (
    <div>
      <PageHeader title="Analytics" subtitle="Funnel, campaigns, revenue and evidence-based reply timing." testid="analytics-header" />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
        <StatCard label="Total Revenue" value={formatINR(data.total_revenue)} icon={TrendingUp} accent="amber" />
        <StatCard label="Lead Sources" value={data.sources.length} accent="blue" />
        <StatCard label="Active Campaigns" value={data.campaigns.length} accent="emerald" />
        <StatCard label="Replies Analysed" value={data.reply_timing?.total||0} accent="purple" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="mb-card chart-card p-5" data-testid="funnel-chart">
          <div className="chart-card__head"><div><span>PIPELINE</span><h2>Lead funnel</h2></div><b>{data.funnel.reduce((s,v)=>s+(v.count||0),0)} signals</b></div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.funnel} layout="vertical" margin={{ left: 8,right:18,top:12,bottom:4 }} barCategoryGap="30%">
              <defs><linearGradient id="funnelBars" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#7c3aed"/><stop offset="55%" stopColor="#6366f1"/><stop offset="100%" stopColor="#22d3ee"/></linearGradient></defs>
              <CartesianGrid horizontal={false} strokeDasharray="4 8"/>
              <XAxis type="number" axisLine={false} tickLine={false}/>
              <YAxis type="category" dataKey="stage" axisLine={false} tickLine={false} width={86}/>
              <Tooltip {...tip} />
              <Bar dataKey="count" fill="url(#funnelBars)" maxBarSize={30} radius={[0,10,10,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="mb-card chart-card p-5" data-testid="sources-chart">
          <div className="chart-card__head"><div><span>ATTRIBUTION</span><h2>Lead sources</h2></div></div>
          <div className="donut-wrap"><ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={data.sources} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={62} outerRadius={94} paddingAngle={5} cornerRadius={9} stroke="none">
                {data.sources.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip {...tip} />
            </PieChart>
          </ResponsiveContainer><div className="donut-center"><strong>{data.sources.reduce((s,v)=>s+(v.value||0),0)}</strong><span>LEADS</span></div></div><div className="chart-legend">{data.sources.map((v,i)=><span key={v.name}><i style={{background:COLORS[i%COLORS.length]}}/>{v.name}<b>{v.value}</b></span>)}</div>
        </div>

        <div className="mb-card chart-card p-5" data-testid="revenue-chart">
          <div className="chart-card__head"><div><span>COMMERCIAL PULSE</span><h2>Revenue trend</h2></div></div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.revenue_trend} margin={{top:16,right:12,left:-6,bottom:0}}><defs><linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={.45}/><stop offset="100%" stopColor="#22d3ee" stopOpacity={0}/></linearGradient></defs>
              <CartesianGrid vertical={false} strokeDasharray="4 8" />
              <XAxis dataKey="month" axisLine={false} tickLine={false}/>
              <YAxis axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 10000000).toFixed(1)}Cr`} />
              <Tooltip {...tip} formatter={(v) => formatINR(v)} />
              <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} fill="url(#revenueFill)" activeDot={{r:6,fill:"#22d3ee",stroke:"#fff",strokeWidth:2}} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="mb-card p-5" data-testid="campaign-table">
          <div className="caption-mono mb-4">Campaign ROI</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] text-slate-500 uppercase border-b border-slate-800">
                <th className="py-2">Campaign</th><th className="py-2">Leads</th><th className="py-2">Won</th><th className="py-2">CPL</th>
              </tr></thead>
              <tbody>
                {data.campaigns.map((c) => (
                  <tr key={c.name} className="border-b border-slate-800/60">
                    <td className="py-2.5 text-slate-200">{c.name}</td>
                    <td className="py-2.5 text-slate-300">{c.leads}</td>
                    <td className="py-2.5 text-emerald-400">{c.won}</td>
                    <td className="py-2.5 text-slate-400">{formatINR(c.cpl)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="mb-card p-5 lg:col-span-2" data-testid="reply-time-chart">
          <div className="flex flex-wrap justify-between gap-3 mb-4"><div><div className="caption-mono">Lead replies by local hour</div><div className="text-xs text-slate-500 mt-1">Stored in {data.reply_timing?.timezone||"Asia/Kolkata"} for campaign planning and agent training.</div></div><div className="text-xs text-purple-300">Best hours: {data.reply_timing?.best_hours?.length?data.reply_timing.best_hours.map(h=>`${h.hour} (${h.replies})`).join(" · "):"Collecting data"}</div></div>
          <ResponsiveContainer width="100%" height={260}><BarChart data={data.reply_timing?.hours||[]} barCategoryGap="34%"><defs><linearGradient id="hourBars" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#a78bfa"/><stop offset="100%" stopColor="#6366f1"/></linearGradient></defs><CartesianGrid vertical={false} strokeDasharray="4 8"/><XAxis dataKey="hour" axisLine={false} tickLine={false} interval={2}/><YAxis axisLine={false} tickLine={false} allowDecimals={false}/><Tooltip {...tip}/><Bar dataKey="replies" fill="url(#hourBars)" maxBarSize={34} radius={[10,10,3,3]}/></BarChart></ResponsiveContainer>
        </div>
        <div className="mb-card chart-card p-5 lg:col-span-2" data-testid="reply-weekday-chart"><div className="chart-card__head"><div><span>WEEKLY RHYTHM</span><h2>Replies by weekday</h2></div></div><ResponsiveContainer width="100%" height={220}><BarChart data={data.reply_timing?.weekdays||[]} barCategoryGap="42%"><defs><linearGradient id="weekdayBars" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#38bdf8"/><stop offset="100%" stopColor="#2563eb"/></linearGradient></defs><CartesianGrid vertical={false} strokeDasharray="4 8"/><XAxis dataKey="day" axisLine={false} tickLine={false}/><YAxis axisLine={false} tickLine={false} allowDecimals={false}/><Tooltip {...tip}/><Bar dataKey="replies" fill="url(#weekdayBars)" maxBarSize={46} radius={[11,11,3,3]}/></BarChart></ResponsiveContainer></div>
      </div>
    </div>
  );
}
